import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 55. FAVORITES
// Les utilisateurs peuvent sauvegarder : entreprises · produits · services ·
// hébergements · activités · professionnels.
// Ces favoris sont ensuite exploitables par Zifek AI, par exemple :
//   « Trouve-moi quelque chose parmi mes favoris. »
// ─────────────────────────────────────────────────────────────

export const FAVORITE_ENTITY_TYPES: { key: string; label: string; icon: string; className: string }[] = [
  { key: 'business', label: 'Entreprise', icon: 'ri-store-2-line', className: 'bg-primary-100 text-primary-900' },
  { key: 'product', label: 'Produit', icon: 'ri-shopping-bag-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'service', label: 'Service', icon: 'ri-service-line', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'accommodation', label: 'Hébergement', icon: 'ri-hotel-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'activity', label: 'Activité', icon: 'ri-run-line', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'professional', label: 'Professionnel', icon: 'ri-user-star-line', className: 'bg-primary-100 text-primary-900' },
];

export function favoriteTypeMeta(key: string | null | undefined) {
  return FAVORITE_ENTITY_TYPES.find((t) => t.key === key) || FAVORITE_ENTITY_TYPES[1];
}

export interface CoreFavorite {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  entity_type: string;
  entity_id: string | null;
  label: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export async function listFavorites(userId: number, filter?: { entityType?: string }): Promise<CoreFavorite[]> {
  let query = supabase
    .from('core_favorites')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (filter?.entityType) query = query.eq('entity_type', filter.entityType);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreFavorite[];
}

export async function isFavorite(userId: number, entityType: string, entityId: string | number): Promise<boolean> {
  const { data } = await supabase
    .from('core_favorites')
    .select('id')
    .eq('user_id', userId)
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId))
    .maybeSingle();
  return !!data?.id;
}

export interface FavoriteInput {
  tenantId?: number | null;
  organizationId?: number | null;
  userId: number;
  entityType: string;
  entityId: string | number;
  label?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** Ajoute un favori (idempotent : renvoie l'id existant si déjà présent). */
export async function addFavorite(input: FavoriteInput): Promise<number | null> {
  const existing = await supabase
    .from('core_favorites')
    .select('id')
    .eq('user_id', input.userId)
    .eq('entity_type', input.entityType)
    .eq('entity_id', String(input.entityId))
    .maybeSingle();
  if (existing.data?.id) return existing.data.id as number;

  const { data, error } = await supabase
    .from('core_favorites')
    .insert({
      tenant_id: input.tenantId ?? null,
      organization_id: input.organizationId ?? null,
      user_id: input.userId,
      entity_type: input.entityType,
      entity_id: String(input.entityId),
      label: input.label || null,
      metadata: input.metadata ?? null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function removeFavorite(userId: number, entityType: string, entityId: string | number): Promise<void> {
  const { error } = await supabase
    .from('core_favorites')
    .delete()
    .eq('user_id', userId)
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId));
  if (error) throw error;
}

export async function deleteFavorite(id: number): Promise<void> {
  const { error } = await supabase.from('core_favorites').delete().eq('id', id);
  if (error) throw error;
}

/** Bascule un favori et renvoie le nouvel état. */
export async function toggleFavorite(input: FavoriteInput): Promise<boolean> {
  const already = await isFavorite(input.userId, input.entityType, input.entityId);
  if (already) {
    await removeFavorite(input.userId, input.entityType, input.entityId);
    return false;
  }
  await addFavorite(input);
  return true;
}

export interface FavoriteStats {
  total: number;
  byType: Record<string, number>;
}

export async function fetchFavoriteStats(userId: number): Promise<FavoriteStats> {
  const rows = await listFavorites(userId);
  const byType: Record<string, number> = {};
  rows.forEach((r) => { byType[r.entity_type] = (byType[r.entity_type] || 0) + 1; });
  return { total: rows.length, byType };
}

/**
 * Résumé textuel des favoris, destiné à Zifek AI.
 * Ex : « Produits favoris : Huile d'argan bio, Sérum… »
 */
export function buildFavoritesSummary(favorites: CoreFavorite[]): string {
  if (favorites.length === 0) return 'Aucun favori enregistré.';
  const groups: Record<string, string[]> = {};
  favorites.forEach((f) => {
    const key = favoriteTypeMeta(f.entity_type).label;
    if (!groups[key]) groups[key] = [];
    if (f.label) groups[key].push(f.label);
  });
  return Object.entries(groups)
    .map(([label, items]) => `${label} : ${items.join(', ')}`)
    .join(' · ');
}